// Import an organisation chart: from a picture/PDF (boxes + lines + OCR, optional Gemma) or from an Excel file.
// Both end in the same editable preview; nothing is created until "Importă".
import React, { useMemo, useRef, useState } from 'react';
import { api } from '../../api';
import { Combobox, Modal, useToast } from '../../ui';
import { Icon } from '../../ui/Icon';
import { loadOcrStatus } from '../Ocr';
import { UNIT_KINDS, type OrgUnit } from './OrgChart';

interface PUnit {
  key: string; name: string; kind: string; parentKey?: string | null; existingParentId?: number | null; existingId?: number | null;
  posts?: number | null; confidence?: number; box?: [number, number, number, number]; aiName?: boolean; ocrName?: string; raw?: string;
}
interface ParseResult { units: PUnit[]; warnings: string[]; source: 'image' | 'excel'; width?: number; height?: number; imageFileId?: string; method?: string; ms?: number; ai?: { used: boolean; enabled: boolean; model?: string; error?: string; corrected?: number } }

const normName = (s: string) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

export function OrgImportModal({ existing, onClose, onDone }: { existing: OrgUnit[]; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [phase, setPhase] = useState<'choose' | 'reading' | 'preview' | 'saving'>('choose');
  const [res, setRes] = useState<ParseResult | null>(null);
  const [units, setUnits] = useState<PUnit[]>([]);
  const [rootMode, setRootMode] = useState<string>('');        // '' = new top level · 'under:<id>' · 'as:<id>' (replaces an existing top unit)
  const rootParent = rootMode.startsWith('under:') ? Number(rootMode.slice(6)) : null;
  const replaceId = rootMode.startsWith('as:') ? Number(rootMode.slice(3)) : null;
  const [sel, setSel] = useState<string | null>(null);
  const [useAi, setUseAi] = useState(true);
  const [aiOn, setAiOn] = useState(false);
  const [readingWhat, setReadingWhat] = useState('');
  const imgInput = useRef<HTMLInputElement>(null), xlsInput = useRef<HTMLInputElement>(null);
  React.useEffect(() => { loadOcrStatus(true).then(s => setAiOn(!!s?.ai.enabled)); }, []);

  const accept = (r: ParseResult) => {
    // units whose name already exists are reused instead of duplicated
    const byName = new Map(existing.map(e => [normName(e.name), e.id]));
    const list = r.units.map(u => ({ ...u, existingId: u.existingId ?? (u as any).existing?.id ?? byName.get(normName(u.name)) ?? null }));
    setRes(r); setUnits(list); setPhase('preview');
    // a chart with a single top unit usually replaces the current top unit (e.g. the default "Conducere")
    const tops = existing.filter(e => e.parentId == null);
    const importTops = list.filter(u => !u.parentKey && !u.existingParentId);
    if (tops.length === 1 && importTops.length === 1 && !importTops[0].existingId) setRootMode('as:' + tops[0].id);
  };
  const fromImage = async (f: File) => {
    setReadingWhat(f.name); setPhase('reading');
    try {
      const up = await api.upload<{ id: string }>(f);
      accept(await api.post<ParseResult>('/api/ocr/orgchart', { fileId: up.id, ai: useAi }));
    } catch (e: any) { toast(e.message, 'error'); setPhase('choose'); }
  };
  const fromExcel = async (f: File) => {
    setReadingWhat(f.name); setPhase('reading');
    try { accept(await api.uploadTo<ParseResult>('/api/auth/departments/import/parse', f)); }
    catch (e: any) { toast(e.message, 'error'); setPhase('choose'); }
  };

  // ---- tree helpers
  const byKey = useMemo(() => new Map(units.map(u => [u.key, u])), [units]);
  const ordered = useMemo(() => {
    const kids = new Map<string | null, PUnit[]>();
    for (const u of units) { const p = u.parentKey && byKey.has(u.parentKey) ? u.parentKey : null; kids.set(p, [...(kids.get(p) || []), u]); }
    const out: { u: PUnit; depth: number }[] = [];
    const seen = new Set<string>();
    const walk = (p: string | null, d: number) => { for (const u of kids.get(p) || []) { if (seen.has(u.key)) continue; seen.add(u.key); out.push({ u, depth: d }); walk(u.key, d + 1); } };
    walk(null, 0);
    for (const u of units) if (!seen.has(u.key)) out.push({ u, depth: 0 });
    return out;
  }, [units, byKey]);
  const descendants = (key: string): Set<string> => {
    const s = new Set<string>([key]); let grew = true;
    while (grew) { grew = false; for (const u of units) if (u.parentKey && s.has(u.parentKey) && !s.has(u.key)) { s.add(u.key); grew = true; } }
    return s;
  };
  const upd = (key: string, patch: Partial<PUnit>) => setUnits(l => l.map(u => u.key === key ? { ...u, ...patch } : u));
  const remove = (key: string) => {
    const u = byKey.get(key);
    setUnits(l => l.filter(x => x.key !== key).map(x => x.parentKey === key ? { ...x, parentKey: u?.parentKey ?? null, existingParentId: u?.parentKey ? null : u?.existingParentId ?? null } : x));
  };
  const addUnit = () => { const key = 'n' + Date.now(); setUnits(l => [...l, { key, name: '', kind: 'Compartiment', parentKey: sel }]); setSel(key); };

  const firstTop = ordered.find(o => o.depth === 0)?.u;
  const doImport = async () => {
    if (units.some(u => !u.name.trim())) { toast('Toate unitățile trebuie să aibă un nume', 'error'); return; }
    setPhase('saving');
    try {
      const r = await api.post<{ created: number; reused: number }>('/api/auth/departments/import', {
        rootParentId: rootParent,
        units: units.map(u => {
          const isTop = !(u.parentKey && byKey.has(u.parentKey)) && !u.existingParentId;
          const takesPlace = replaceId != null && isTop && u.key === firstTop?.key;
          return { key: u.key, name: u.name.trim(), kind: u.kind, parentKey: u.parentKey && byKey.has(u.parentKey) ? u.parentKey : null,
            existingParentId: u.parentKey ? null : u.existingParentId ?? (replaceId != null && isTop && !takesPlace ? replaceId : null),
            existingId: takesPlace ? replaceId : u.existingId ?? null, rename: takesPlace };
        }),
      });
      toast(`Organigrama a fost importată: ${r.created} unități noi${r.reused ? `, ${r.reused} existente refolosite` : ''}`, 'ok');
      onDone();
    } catch (e: any) { toast(e.message, 'error'); setPhase('preview'); }
  };

  const low = (u: PUnit) => u.confidence != null && u.confidence < 70 && !u.aiName;
  const existingOpts = existing.map(e => ({ value: 'e' + e.id, label: e.name, sub: 'există în DocFlow' }));

  // ---------------------------------------------------------------- choose
  if (phase === 'choose' || phase === 'reading') return (
    <Modal title="Importă organigrama" icon="org" size="lg" onClose={onClose} footer={<><span className="grow" /><button className="btn" onClick={onClose}>Renunță</button></>}>
      {phase === 'reading' ? (
        <div className="imp-reading"><span className="spinner" /><div><b>Se analizează {readingWhat}…</b><div className="muted small">Se caută casetele și liniile de subordonare, apoi se citește textul fiecărei casete. Pentru o organigramă mare poate dura un minut.</div></div></div>
      ) : (
        <div className="imp-choices">
          <div className="imp-choice">
            <div className="imp-ico"><Icon name="scan" size={22} /></div>
            <h3>Din imagine sau PDF</h3>
            <p className="muted small">O organigramă scanată, fotografiată sau exportată (PNG, JPG, PDF). DocFlow găsește casetele, urmărește liniile de subordonare și citește denumirile (inclusiv textul vertical).</p>
            <label className="checkbox small"><input type="checkbox" checked={useAi && aiOn} disabled={!aiOn} onChange={e => setUseAi(e.target.checked)} /><span>{aiOn ? 'Corectează denumirile cu Gemma' : 'Gemma nu este activ (Administrare → OCR)'}</span></label>
            <button className="btn primary" onClick={() => imgInput.current?.click()}><Icon name="upload" />Alege imaginea sau PDF-ul</button>
            <input ref={imgInput} type="file" hidden accept="image/png,image/jpeg,image/gif,image/bmp,application/pdf" onChange={e => { const f = e.target.files?.[0]; if (f) fromImage(f); e.currentTarget.value = ''; }} />
            <span className="muted small">Rezultatul se verifică în previzualizare înainte de import.</span>
          </div>
          <div className="imp-choice">
            <div className="imp-ico xls"><Icon name="list" size={22} /></div>
            <h3>Din Excel</h3>
            <p className="muted small">Un tabel cu coloanele <b>Unitate</b>, <b>Subordonată lui</b> și opțional <b>Tip</b> (Conducere, Direcție, Serviciu, Birou, Compartiment). Rezultat exact, fără recunoaștere.</p>
            <a className="btn ghost sm" href={api.authUrl('/api/auth/departments/import/template')} download><Icon name="download" />Descarcă modelul Excel</a>
            <button className="btn primary" onClick={() => xlsInput.current?.click()}><Icon name="upload" />Alege fișierul Excel</button>
            <input ref={xlsInput} type="file" hidden accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv" onChange={e => { const f = e.target.files?.[0]; if (f) fromExcel(f); e.currentTarget.value = ''; }} />
            <span className="muted small">Formate: .xlsx sau .csv</span>
          </div>
        </div>
      )}
    </Modal>
  );

  // ---------------------------------------------------------------- preview
  const r = res!;
  const newCount = units.filter(u => !u.existingId).length;
  return (
    <Modal title="Importă organigrama — verificare" icon="org" size="xl" onClose={onClose}
      footer={<>
        <span className="muted small">{units.length} unități · {newCount} noi{units.length - newCount ? ` · ${units.length - newCount} existente (refolosite)` : ''}</span>
        <span className="grow" />
        <button className="btn" onClick={() => setPhase('choose')} disabled={phase === 'saving'}>Înapoi</button>
        <button className="btn primary" onClick={doImport} disabled={phase === 'saving' || units.length === 0}><Icon name="check" />{phase === 'saving' ? 'Se importă…' : `Importă ${newCount} unități`}</button>
      </>}>
      <div className="stack" style={{ gap: 10 }}>
        <div className="row" style={{ flexWrap: 'wrap', gap: 10 }}>
          <span className="imp-src"><Icon name={r.source === 'excel' ? 'list' : 'scan'} size={14} />{r.source === 'excel' ? 'Din Excel' : r.method === 'ai' ? `Citită de ${r.ai?.model}` : 'Recunoaștere casete + OCR'}{r.ms ? ` · ${(r.ms / 1000).toFixed(0)} s` : ''}{r.ai?.used && r.ai.corrected ? ` · ${r.ai.corrected} denumiri corectate de ${r.ai.model}` : ''}</span>
          {r.ai?.error && <span className="warn-text small" title={r.ai.error}>Gemma indisponibil — doar OCR</span>}
          <span className="grow" />
          <label className="row small" style={{ gap: 6 }}>Vârful organigramei importate se așază
            <select className="select" style={{ width: 330 }} value={rootMode} onChange={e => setRootMode(e.target.value)}>
              <option value="">la nivelul cel mai de sus (unitate nouă)</option>
              {existing.filter(e => e.parentId == null).map(e => <option key={'as' + e.id} value={'as:' + e.id}>în locul unității „{e.name}” (o redenumește)</option>)}
              {existing.map(e => <option key={e.id} value={'under:' + e.id}>sub „{e.name}”</option>)}
            </select>
          </label>
        </div>
        {r.warnings.length > 0 && <div className="banner warn small"><Icon name="alert" size={15} /><div>{r.warnings.map((w, i) => <div key={i}>{w}</div>)}</div></div>}
        <div className={'imp-preview ' + (r.imageFileId ? 'with-image' : '')}>
          {r.imageFileId && r.width && (
            <div className="imp-image">
              <div className="imp-canvas" style={{ aspectRatio: `${r.width} / ${r.height}` }}>
                <img src={api.fileUrl(r.imageFileId, 'raw')} alt="organigramă" />
                {units.filter(u => u.box).map(u => {
                  const [x, y, w, h] = u.box!;
                  const n = ordered.findIndex(o => o.u.key === u.key) + 1;
                  return <button key={u.key} className={'imp-box ' + (sel === u.key ? 'sel ' : '') + (low(u) ? 'low' : '')} title={u.name}
                    style={{ left: `${(x / r.width!) * 100}%`, top: `${(y / r.height!) * 100}%`, width: `${(w / r.width!) * 100}%`, height: `${(h / r.height!) * 100}%` }}
                    onClick={() => { setSel(u.key); document.getElementById('imp-' + u.key)?.scrollIntoView({ block: 'nearest' }); }}><span>{n}</span></button>;
                })}
              </div>
              <span className="muted small">Clic pe o casetă pentru a o găsi în listă. Galben = citire nesigură.</span>
            </div>
          )}
          <div className="imp-list">
            <div className="imp-row head"><span>#</span><span>Denumire</span><span>Tip</span><span>Subordonată lui</span><span /></div>
            {ordered.map(({ u, depth }, i) => {
              const forbidden = descendants(u.key);
              const parentVal = u.parentKey && byKey.has(u.parentKey) ? u.parentKey : u.existingParentId ? 'e' + u.existingParentId : null;
              return (
                <div key={u.key} id={'imp-' + u.key} className={'imp-row ' + (sel === u.key ? 'sel ' : '') + (low(u) ? 'low' : '')} onClick={() => setSel(u.key)}>
                  <span className="n">{i + 1}</span>
                  <span className="nm" style={{ paddingLeft: Math.min(depth, 6) * 16 }}>
                    {depth > 0 && <span className="branch">└</span>}
                    <input className="input" value={u.name} placeholder="Denumirea unității" onChange={e => upd(u.key, { name: e.target.value, existingId: null })} title={u.ocrName ? `Citit de OCR: ${u.ocrName}` : u.raw ? `Text citit: ${u.raw}` : undefined} />
                    {replaceId != null && u.key === firstTop?.key ? <span className="tag adm-tag-ok" title="Unitatea de vârf existentă va primi acest nume">înlocuiește „{existing.find(e => e.id === replaceId)?.name}”</span>
                      : u.existingId ? <span className="tag adm-tag-ok" title="Există deja o unitate cu acest nume — va fi refolosită">există</span> : null}
                    {u.aiName && <span className="ocr-badge AI" title={u.ocrName ? `OCR: ${u.ocrName}` : 'citit de Gemma'}><Icon name="sparkles" size={10} />AI</span>}
                    {u.posts != null && <span className="muted small" title="Număr de posturi (informativ)">{u.posts} p.</span>}
                  </span>
                  <span><select className="select" value={u.kind} onChange={e => upd(u.key, { kind: e.target.value })}>{UNIT_KINDS.map(k => <option key={k}>{k}</option>)}</select></span>
                  <span>
                    <Combobox value={parentVal} placeholder={rootParent ? 'sub unitatea aleasă' : replaceId != null ? '— vârf (existent) —' : '— vârf —'}
                      options={[...units.filter(x => !forbidden.has(x.key)).map(x => ({ value: x.key, label: x.name || '(fără nume)' })), ...existingOpts]}
                      onChange={v => { const s = v == null ? null : String(v); upd(u.key, s && s.startsWith('e') && !byKey.has(s) ? { parentKey: null, existingParentId: Number(s.slice(1)) } : { parentKey: s, existingParentId: null }); }} />
                  </span>
                  <span><button className="icon-btn" title="Elimină (subordonații urcă un nivel)" onClick={e => { e.stopPropagation(); remove(u.key); }}><Icon name="trash" size={15} /></button></span>
                </div>
              );
            })}
            <div><button className="btn sm ghost" onClick={addUnit}><Icon name="plus" />Adaugă o unitate</button></div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
